import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useToggle } from '@uidotdev/usehooks';
import { useForm, Controller } from 'react-hook-form';
import { TextField, Box, Modal, Typography } from '@mui/material';

import { AuthTemplate } from '@components/auth-template';
import { Icon } from '@components/icon';
import { forgotPassword, verifyResetToken, resetPassword } from '@service/current-user';

import * as S from './styles';

export function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('token');
  
  const [step, setStep] = useState(urlToken ? 2 : 0);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState(urlToken || '');
  const [failedAttempts, setFailedAttempts] = useState(0);

  const [open, toggleModal] = useToggle(false);
  const [modalText, setModalText] = React.useState("");

  const navigate = useNavigate();

  const formEmail = useForm({ defaultValues: { email: '' } });
  const formToken = useForm({ defaultValues: { token: '' } });
  const formPassword = useForm({ defaultValues: { newPassword: '', confirmPassword: '' } });

  const handleShowError = (message: string) => {
    setModalText(message);
    if (!open) toggleModal();
  };

  const mutationForgot = useMutation({
    mutationFn: forgotPassword,
    onSuccess: () => {
      setStep(1);
    },
    onError: (error: any) => {
      handleShowError(error?.response?.data?.message || "Erro ao solicitar recuperação.");
    }
  });

  const mutationVerify = useMutation({
    mutationFn: (data: { email: string, token: string }) => verifyResetToken(data.email, data.token),
    onSuccess: (_, variables) => {
      setToken(variables.token);
      setStep(2);
    },
    onError: (error: any) => {
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      if (newAttempts >= 3) {
        navigate('/');
      } else {
        handleShowError(`Código inválido. Tentativas restantes: ${3 - newAttempts}`);
      }
    }
  });

  const mutationReset = useMutation({
    mutationFn: (data: { email: string, token: string, newPassword: string }) => 
      resetPassword(data.email, data.token, data.newPassword),
    onSuccess: () => {
      navigate('/sign-in');
    },
    onError: (error: any) => {
      handleShowError(error?.response?.data?.message || "Erro ao redefinir senha.");
    }
  });

  const onEmailSubmit = formEmail.handleSubmit((data) => {
    setEmail(data.email);
    mutationForgot.mutate(data.email);
  });

  const onTokenSubmit = formToken.handleSubmit((data) => {
    mutationVerify.mutate({ email, token: data.token });
  });

  const onPasswordSubmit = formPassword.handleSubmit((data) => {
    if (data.newPassword !== data.confirmPassword) {
      handleShowError("As senhas não coincidem.");
      return;
    }
    mutationReset.mutate({ email, token, newPassword: data.newPassword });
  });

  let title = "Recuperar Senha";
  let buttonText = "Enviar Email";
  let onSubmit = onEmailSubmit;
  let description = "Informe seu email para enviarmos um código de recuperação.";

  if (step === 1) {
    title = "Verificar Código";
    buttonText = "Validar Código";
    onSubmit = onTokenSubmit;
    description = "Insira o código de 6 dígitos que enviamos para o seu email.";
  } else if (step === 2) {
    title = "Nova Senha";
    buttonText = "Redefinir Senha";
    onSubmit = onPasswordSubmit;
    description = "Crie uma nova senha para acessar sua conta.";
  }

  const handleKeyDown = (e: React.KeyboardEvent, submitFn: () => void) => {
    if (e.key === 'Enter') submitFn();
  };

  const isLoading = step === 0 ? mutationForgot.isPending : step === 1 ? mutationVerify.isPending : mutationReset.isPending;

  return (
    <>
      <AuthTemplate title={title} buttonText={buttonText} buttonOnClick={onSubmit} isLoading={isLoading}>
        <S.Description lineHeight={1} mb={4}>
          {description}
        </S.Description>

        {step === 0 && (
          <Controller
            name="email"
            control={formEmail.control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                variant="outlined"
                fullWidth
                size="small"
                sx={{ mb: 3 }}
                onKeyDown={(e) => handleKeyDown(e, onEmailSubmit)}
              />
            )}
          />
        )}

        {step === 1 && (
          <Controller
            name="token"
            control={formToken.control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Código de 6 dígitos"
                variant="outlined"
                fullWidth
                size="small"
                sx={{ mb: 3 }}
                onKeyDown={(e) => handleKeyDown(e, onTokenSubmit)}
              />
            )}
          />
        )}

        {step === 2 && (
          <>
            <Controller
              name="newPassword"
              control={formPassword.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="password"
                  label="Nova Senha"
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={{ mb: 3 }}
                  onKeyDown={(e) => handleKeyDown(e, onPasswordSubmit)}
                />
              )}
            />
            <Controller
              name="confirmPassword"
              control={formPassword.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="password"
                  label="Confirmar Nova Senha"
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={{ mb: 3 }}
                  onKeyDown={(e) => handleKeyDown(e, onPasswordSubmit)}
                />
              )}
            />
          </>
        )}
        
        <Box display="flex" justifyContent="flex-start" mt={1}>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => navigate('/sign-in')}
          >
            Voltar para o Login
          </Typography>
        </Box>
      </AuthTemplate>

      <Modal open={open} onClose={toggleModal}>
        <S.ModalContainer>
          <Box maxWidth={200} display="flex" flexDirection="column" alignItems="center">
            <Icon disableTheming name="error" alt="error icon" width={36} />
            <Typography variant="h5" color="primary" fontWeight={600} textAlign="center" mt={2}>
              {modalText}
            </Typography>
            <S.Button onClick={toggleModal}>OK</S.Button>
          </Box>
        </S.ModalContainer>
      </Modal>
    </>
  );
}
